<template>
    <Transition name="search-modal">
        <div
            v-if="isVisible"
            class="search-overlay"
            @click.self="handleClose"
        >
            <div class="search-container">
                <div class="search-header">
                    <div class="search-input-wrapper">
                        <img
                            class="search-icon"
                            src="/search_icon.svg"
                        />
                        <input
                            ref="searchInputRef"
                            v-model="searchQuery"
                            type="text"
                            placeholder="Search for pages, orders, references, email, phone number, or postcode..."
                            class="search-input"
                            @keydown="handleKeyDown"
                        />
                        <div class="shortcut-badge">
                            {{ shortcutText }}
                        </div>
                    </div>
                </div>

                <div
                    v-if="isLoading"
                    class="loading-container"
                >
                    <span>Searching...</span>
                </div>

                <div
                    v-else-if="searchResults.length > 0"
                    class="search-results"
                    ref="searchResultsRef"
                    @scroll="handleScroll"
                >
                    <div class="results-list">
                        <div
                            v-for="(result, index) in searchResults"
                            :key="index"
                            class="result-item"
                            :data-index="index"
                            :class="{
                                'result-item-active': selectedIndex === index,
                                'result-item-expanded': expandedIndex === index,
                            }"
                            @click="handleResultClick(result, index)"
                            @mouseenter="selectedIndex = index"
                        >
                            <div class="result-main">
                                <div class="result-header">
                                    <span class="result-reference">{{
                                        result.title
                                    }}</span>
                                    <span
                                        v-if="result.status"
                                        class="result-status"
                                        :style="{
                                            backgroundColor: result.statusColor,
                                        }"
                                    >
                                        {{ result.status }}
                                    </span>
                                </div>
                                <div
                                    v-if="result.type === 'order'"
                                    class="result-details"
                                >
                                    <div
                                        v-if="result.address"
                                        class="result-address"
                                    >
                                        <svg
                                            class="result-icon"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                            />
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                        </svg>
                                        <span>{{ result.address }}</span>
                                    </div>
                                    <div
                                        v-if="result.postcode"
                                        class="result-postcode"
                                    >
                                        <svg
                                            class="result-icon"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                            />
                                        </svg>
                                        <span>{{ result.postcode }}</span>
                                    </div>
                                    <div
                                        v-if="result.timeslot"
                                        class="result-timeslot"
                                    >
                                        <svg
                                            class="result-icon"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        <span>{{ result.timeslot }}</span>
                                    </div>
                                    <div
                                        v-if="result.items"
                                        class="result-items"
                                    >
                                        <span class="result-items-label"
                                            >Items:</span
                                        >
                                        <span class="result-items-list">{{
                                            result.items
                                        }}</span>
                                    </div>
                                </div>
                                <div
                                    v-else-if="result.subtitle"
                                    class="result-details"
                                >
                                    <span>{{ result.subtitle }}</span>
                                </div>
                                <div
                                    v-if="
                                        result.navigationOptions &&
                                        result.navigationOptions.length > 1
                                    "
                                    class="result-actions"
                                >
                                    <div
                                        v-for="(
                                            option, optIndex
                                        ) in result.navigationOptions"
                                        :key="optIndex"
                                        class="navigation-option"
                                        :class="{
                                            'navigation-option-active':
                                                expandedIndex === index &&
                                                selectedButtonIndex ===
                                                    optIndex,
                                        }"
                                        @click.stop="navigateTo(option.route)"
                                    >
                                        {{ option.label }}
                                    </div>
                                </div>
                                <div
                                    v-else-if="
                                        result.navigationOptions &&
                                        result.navigationOptions.length === 1
                                    "
                                    class="result-actions"
                                >
                                    <div
                                        class="navigation-option"
                                        :class="{
                                            'navigation-option-active':
                                                expandedIndex === index &&
                                                selectedButtonIndex === 0,
                                        }"
                                        @click.stop="
                                            navigateTo(
                                                result.navigationOptions![0]
                                                    .route,
                                            )
                                        "
                                    >
                                        {{ result.navigationOptions![0].label }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="isLoadingMore"
                        class="loading-more"
                    >
                        <span>Loading more...</span>
                    </div>
                </div>

                <div
                    v-else-if="searchQuery && !isLoading"
                    class="no-results"
                >
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    <p>No results found</p>
                </div>

                <div
                    v-if="!searchQuery && !isLoading"
                    class="search-tips"
                >
                    <div class="tip">
                        <span>Navigate results</span>
                        <kbd>↑</kbd>
                        <kbd>↓</kbd>
                    </div>
                    <div class="tip">
                        <span>Expand / Select</span>
                        <kbd>↵</kbd>
                    </div>
                    <div class="tip">
                        <span>Navigate buttons</span>
                        <kbd>←</kbd>
                        <kbd>→</kbd>
                    </div>
                    <div class="tip">
                        <span>Close</span>
                        <kbd>ESC</kbd>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue'
import { BookOrder } from '../types'
import { getQueryType, QueryType } from '../modules/util'
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore'
import { useRouter } from 'vue-router'
import {
    isFirebaseId,
    isReference,
    isContactQuery,
    isExternalReference,
    isRecurringBatchReference,
    searchOrders as searchOrdersComposable,
    searchBeehivePages as searchBeehivePagesComposable,
} from '../composables/useSearchLogic'
import {
    loadJobStatusLookup,
    resolveDisplayTaskStatusWithLookup,
    type JobStatusLookup,
} from '@/lib/fulfilment/taskStatusMapping'
import { cleanseLinkFor } from '@/lib/cleanse/cleanse-link'

interface NavigationOption {
    label: string
    route: string
}

interface SearchResult {
    title: string
    subtitle?: string
    type: 'page' | 'order'
    recordId?: string
    navigationOptions?: NavigationOption[]
    status?: string
    statusColor?: string
    address?: string
    postcode?: string
    timeslot?: string
    items?: string
}

export default {
    name: 'GlobalSearch',
    setup() {
        const router = useRouter()
        const isVisible = ref(false)
        const searchQuery = ref('')
        const searchResults = ref<SearchResult[]>([])
        const isLoading = ref(false)
        const isLoadingMore = ref(false)
        const lastDocument = ref<QueryDocumentSnapshot<DocumentData> | null>(
            null,
        )
        const hasMore = ref(false)
        const selectedIndex = ref(-1)
        const expandedIndex = ref(-1)
        const selectedButtonIndex = ref(-1)
        const searchInputRef = ref<HTMLInputElement | null>(null)
        const searchResultsRef = ref<HTMLElement | null>(null)
        const jobStatusLookup = ref<JobStatusLookup | null>(null)

        onMounted(async () => {
            try {
                jobStatusLookup.value = await loadJobStatusLookup()
            } catch (error) {
                console.error(
                    'Failed to load job statuses for global search',
                    error,
                )
            }
        })

        const shortcutText = computed(() => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
            return isMac ? '⌘K' : 'Ctrl+K'
        })

        /**
         * Formats BookOrder to SearchResult for GlobalSearch display
         */
        const formatOrderToSearchResult = (
            order: BookOrder,
            docId?: string,
        ): SearchResult => {
            const recordId = order.recordId || docId || ''
            const reference = order.LTA || order.quote_ref || 'No reference'
            const userIdentifier =
                order.userDetails?.email ||
                order.userDetails?.mobilenumber ||
                order.userDetails?.name ||
                'N/A'

            const addressParts = []
            if (order.address_details?.line_1)
                addressParts.push(order.address_details.line_1)
            if (order.address_details?.line_2)
                addressParts.push(order.address_details.line_2)
            const address =
                addressParts.length > 0 ? addressParts.join(', ') : undefined
            const postcode = order.address_details?.postcode

            const timeslot = order.selectedDateAndTime
                ? new Date(
                      order.selectedDateAndTime.seconds * 1000,
                  ).toLocaleString()
                : undefined

            const items = order?.items
                ?.map(item => item['LITTA Service'] || '')
                .filter(Boolean)
                .join(', ')

            const displayStatus = resolveDisplayTaskStatusWithLookup(
                order,
                jobStatusLookup.value,
            )
            const status =
                displayStatus &&
                displayStatus !== 'Unknown' &&
                displayStatus !== 'Loading...'
                    ? displayStatus
                    : order.LTA
                      ? 'Not assigned'
                      : 'Unpaid quote'

            // Default status color, can be enhanced with actual status colors
            const statusColor = '#6b7280'

            const cleanseLink = cleanseLinkFor(recordId, order.isImageCleansed)
            const navigationOptions: NavigationOption[] = [
                {
                    label: 'View',
                    route: `/view/${recordId}`,
                },
                {
                    label: 'Post Review',
                    route: `/postreview/${recordId}`,
                },
                {
                    label: cleanseLink.label,
                    route: cleanseLink.path,
                },
            ]

            // Add Quote Builder if conditions are met
            const enquiryType = order.enquiry_type
            const clientType = order.client_type
            const shouldShowQuoteBuilder =
                enquiryType === 'residential' ||
                enquiryType === 'business' ||
                clientType === 'businessPro'

            if (shouldShowQuoteBuilder) {
                navigationOptions.push({
                    label: 'Quote Builder',
                    route: `/quotebuilder/${recordId}`,
                })
            }

            return {
                title: reference,
                subtitle: userIdentifier,
                type: 'order' as const,
                recordId,
                status,
                statusColor,
                address,
                postcode,
                timeslot,
                items,
                navigationOptions,
            }
        }

        /**
         * Searches beehive pages and formats results for GlobalSearch
         */
        const searchBeehivePages = async (
            query: string,
        ): Promise<SearchResult[]> => {
            const pageResults = await searchBeehivePagesComposable(query)
            return pageResults.map(page => ({
                title: page.title,
                subtitle: page.subtitle,
                type: 'page' as const,
                navigationOptions: page.navigationOptions,
            }))
        }

        /**
         * Searches orders and formats results for GlobalSearch
         */
        const searchOrders = async (
            searchValue: string,
            lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
        ): Promise<{
            results: SearchResult[]
            lastDoc: QueryDocumentSnapshot<DocumentData> | null
            hasMore: boolean
        }> => {
            const {
                results: orders,
                lastDoc: newLastDoc,
                hasMore: moreResults,
            } = await searchOrdersComposable(searchValue, lastDoc, 11)

            if (!jobStatusLookup.value) {
                try {
                    jobStatusLookup.value = await loadJobStatusLookup()
                } catch (error) {
                    console.error(
                        'Failed to load job statuses for global search',
                        error,
                    )
                }
            }

            const formattedResults: SearchResult[] = orders.map(order =>
                formatOrderToSearchResult(order),
            )

            return {
                results: formattedResults,
                lastDoc: newLastDoc,
                hasMore: moreResults,
            }
        }

        const performSearch = async () => {
            const query = searchQuery.value.trim()

            if (!query) {
                searchResults.value = []
                lastDocument.value = null
                hasMore.value = false
                return
            }

            isLoading.value = true
            searchResults.value = []
            lastDocument.value = null
            hasMore.value = false
            expandedIndex.value = -1
            selectedButtonIndex.value = -1

            try {
                // Check if it's an external reference (L- prefix)
                const isExternalRef = isExternalReference(query)

                // Check if it's a reference (LTA, LTC, LTB, LTP) or quote reference (LRQ, LPQ, LCQ, LBQ)
                const isRef = isReference(query)

                // Check if it looks like a Firebase ID
                const isFirebaseIdValue = isFirebaseId(query)

                // Check if it's email, phone number, or postcode
                const queryType = getQueryType(query)
                const isEmailOrPostcode = isContactQuery(queryType)

                if (
                    isExternalRef ||
                    isRef ||
                    isRecurringBatchReference(query) ||
                    isFirebaseIdValue ||
                    isEmailOrPostcode
                ) {
                    // Search for orders
                    const {
                        results,
                        lastDoc,
                        hasMore: moreResults,
                    } = await searchOrders(query)
                    searchResults.value = results
                    lastDocument.value = lastDoc
                    hasMore.value = moreResults

                    // If no order results found and it's not a reference/email/phone/postcode, also search pages
                    if (
                        results.length === 0 &&
                        !isRef &&
                        !isExternalRef &&
                        !isEmailOrPostcode
                    ) {
                        const pageResults = await searchBeehivePages(query)
                        searchResults.value = pageResults
                    }
                } else {
                    // Search for beehive pages
                    const pageResults = await searchBeehivePages(query)
                    searchResults.value = pageResults
                }
            } catch (error) {
                console.error('Error performing search:', error)
            } finally {
                isLoading.value = false
            }
        }

        const loadMore = async () => {
            if (isLoadingMore.value || !hasMore.value || !lastDocument.value)
                return

            const query = searchQuery.value.trim()
            if (!query) return

            isLoadingMore.value = true

            try {
                const {
                    results,
                    lastDoc,
                    hasMore: moreResults,
                } = await searchOrders(query, lastDocument.value)
                searchResults.value = [...searchResults.value, ...results]
                lastDocument.value = lastDoc
                hasMore.value = moreResults
            } catch (error) {
                console.error('Error loading more results:', error)
            } finally {
                isLoadingMore.value = false
            }
        }

        const handleScroll = (event: Event) => {
            const target = event.target as HTMLElement
            if (!target) return

            const scrollTop = target.scrollTop
            const scrollHeight = target.scrollHeight
            const clientHeight = target.clientHeight

            // Check if user is near the bottom (within 50px)
            if (scrollHeight - scrollTop - clientHeight < 50) {
                loadMore()
            }
        }

        const scrollToSelectedItem = () => {
            if (selectedIndex.value < 0 || !searchResultsRef.value) return

            nextTick(() => {
                const selectedElement = searchResultsRef.value?.querySelector(
                    `[data-index="${selectedIndex.value}"]`,
                ) as HTMLElement

                if (selectedElement && searchResultsRef.value) {
                    const container = searchResultsRef.value
                    const elementTop = selectedElement.offsetTop
                    const elementBottom =
                        elementTop + selectedElement.offsetHeight
                    const containerTop = container.scrollTop
                    const containerBottom =
                        containerTop + container.clientHeight

                    // Scroll if element is above the visible area
                    if (elementTop < containerTop) {
                        container.scrollTop = elementTop - 8 // Add small padding
                    }
                    // Scroll if element is below the visible area
                    else if (elementBottom > containerBottom) {
                        container.scrollTop =
                            elementBottom - container.clientHeight + 8 // Add small padding
                    }
                }
            })
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            // If a result is expanded, navigate between buttons
            if (expandedIndex.value >= 0) {
                const currentResult = searchResults.value[expandedIndex.value]
                if (!currentResult || !currentResult.navigationOptions) return

                const buttonCount = currentResult.navigationOptions.length

                if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                    event.preventDefault()
                    selectedButtonIndex.value = Math.min(
                        selectedButtonIndex.value + 1,
                        buttonCount - 1,
                    )
                } else if (
                    event.key === 'ArrowLeft' ||
                    event.key === 'ArrowUp'
                ) {
                    event.preventDefault()
                    selectedButtonIndex.value = Math.max(
                        selectedButtonIndex.value - 1,
                        0,
                    )
                } else if (event.key === 'Enter') {
                    event.preventDefault()
                    if (
                        selectedButtonIndex.value >= 0 &&
                        selectedButtonIndex.value < buttonCount
                    ) {
                        navigateTo(
                            currentResult.navigationOptions[
                                selectedButtonIndex.value
                            ].route,
                        )
                    }
                } else if (event.key === 'Escape') {
                    event.preventDefault()
                    expandedIndex.value = -1
                    selectedButtonIndex.value = -1
                }
                return
            }

            // Normal navigation between results
            if (event.key === 'ArrowDown') {
                event.preventDefault()
                selectedIndex.value = Math.min(
                    selectedIndex.value + 1,
                    searchResults.value.length - 1,
                )
                expandedIndex.value = -1
                selectedButtonIndex.value = -1
                scrollToSelectedItem()
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                selectedIndex.value = Math.max(selectedIndex.value - 1, -1)
                expandedIndex.value = -1
                selectedButtonIndex.value = -1
                scrollToSelectedItem()
            } else if (event.key === 'Enter') {
                event.preventDefault()
                const resultIndex =
                    selectedIndex.value >= 0
                        ? selectedIndex.value
                        : searchResults.value.length === 1
                          ? 0
                          : -1

                if (resultIndex >= 0 && searchResults.value[resultIndex]) {
                    const result = searchResults.value[resultIndex]
                    if (
                        result.navigationOptions &&
                        result.navigationOptions.length > 0
                    ) {
                        // Expand the result to show navigation buttons
                        expandedIndex.value = resultIndex
                        selectedButtonIndex.value = 0
                    }
                }
            } else if (event.key === 'Escape') {
                handleClose()
            }
        }

        const handleResultClick = (result: SearchResult, index: number) => {
            // On click, expand the result if it has navigation options
            if (
                result.navigationOptions &&
                result.navigationOptions.length > 0
            ) {
                if (expandedIndex.value === index) {
                    // If already expanded, navigate to first option
                    navigateTo(result.navigationOptions[0].route)
                } else {
                    // Expand to show navigation buttons
                    expandedIndex.value = index
                    selectedButtonIndex.value = 0
                }
            }
        }

        const navigateTo = (route: string) => {
            if (route.startsWith('http')) {
                window.location.href = route
            } else {
                router.push(route)
            }
            handleClose()
        }

        const handleClose = () => {
            isVisible.value = false
            searchQuery.value = ''
            searchResults.value = []
            selectedIndex.value = -1
            expandedIndex.value = -1
            selectedButtonIndex.value = -1
            lastDocument.value = null
            hasMore.value = false
        }

        const openSearch = () => {
            isVisible.value = true
            nextTick(() => {
                searchInputRef.value?.focus()
            })
        }

        const handleKeyboardShortcut = (event: KeyboardEvent) => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
            const isCtrlK = !isMac && event.ctrlKey && event.key === 'k'
            const isCmdK = isMac && event.metaKey && event.key === 'k'

            if (isCtrlK || isCmdK) {
                event.preventDefault()
                openSearch()
            }
        }

        onMounted(() => {
            window.addEventListener('keydown', handleKeyboardShortcut)
        })

        onUnmounted(() => {
            window.removeEventListener('keydown', handleKeyboardShortcut)
        })

        // Debounce search
        let searchTimeout: ReturnType<typeof setTimeout> | null = null
        watch(searchQuery, () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout)
            }
            searchTimeout = setTimeout(() => {
                performSearch()
            }, 300)
        })

        return {
            isVisible,
            searchQuery,
            searchResults,
            isLoading,
            selectedIndex,
            searchInputRef,
            handleKeyDown,
            handleResultClick,
            navigateTo,
            handleClose,
            openSearch,
            shortcutText,
            isLoadingMore,
            hasMore,
            loadMore,
            handleScroll,
            expandedIndex,
            selectedButtonIndex,
            searchResultsRef,
        }
    },
}
</script>

<style scoped>
.search-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 10vh;
    z-index: 1000002;
}

.search-container {
    width: 90%;
    max-width: 640px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 70vh;
}

.search-header {
    padding: 16px;
    border-bottom: 1px solid #e5e7eb;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
}

.search-icon {
    width: 20px;
    height: 20px;
    color: #9ca3af;
    flex-shrink: 0;
}

.search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 16px;
    color: #1f2937;
    background: transparent;
}

.search-input::placeholder {
    color: #9ca3af;
}

.shortcut-badge {
    padding: 4px 8px;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 12px;
    font-family:
        system-ui,
        -apple-system,
        sans-serif;
    color: #6b7280;
}

.search-results {
    overflow-y: auto;
    max-height: calc(70vh - 120px);
}

.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    color: #9ca3af;
}

.loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 24px;
    color: #9ca3af;
    font-size: 13px;
}

.no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    color: #9ca3af;
}

.no-results svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.no-results p {
    font-size: 14px;
    margin: 0;
}

.results-list {
    padding: 8px;
}

.result-item {
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    border: 1px solid transparent;
}

.result-item:hover,
.result-item-active {
    background-color: #f3f4f6;
}

.result-item-expanded {
    background-color: #f9fafb;
    border-color: #3b82f6;
}

.result-main {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.result-header {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.result-reference {
    font-weight: 600;
    font-size: 14px;
    color: #1f2937;
}

.result-status {
    font-size: 12px;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
}

.result-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
    color: #6b7280;
}

.result-address,
.result-postcode,
.result-timeslot {
    display: flex;
    align-items: center;
    gap: 6px;
}

.result-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

.result-items {
    font-size: 12px;
    color: #9ca3af;
    display: flex;
    gap: 4px;
}

.result-items-label {
    font-weight: 600;
}

.result-items-list {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.result-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    flex-wrap: wrap;
}

.navigation-option {
    padding: 4px 12px;
    background-color: #06cccc;
    color: white;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.navigation-option-active {
    background-color: #3b82f6;
    color: white;
    border: 1px solid #3b82f6;
}

.navigation-option:hover {
    background-color: #04a4a4;
}

.search-tips {
    padding: 16px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    background: #f9fafb;
}

.tip {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #6b7280;
}

.tip kbd {
    padding: 4px 6px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 11px;
    font-family:
        system-ui,
        -apple-system,
        sans-serif;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Animations */
.search-modal-enter-active,
.search-modal-leave-active {
    transition: opacity 0.2s ease;
}

.search-modal-enter-active .search-container,
.search-modal-leave-active .search-container {
    transition:
        transform 0.2s ease,
        opacity 0.2s ease;
}

.search-modal-enter-from,
.search-modal-leave-to {
    opacity: 0;
}

.search-modal-enter-from .search-container,
.search-modal-leave-to .search-container {
    transform: translateY(-20px);
    opacity: 0;
}
</style>
